import * as Sentry from '@sentry/capacitor'
import * as SentrySibling from '@sentry/vue'
import { captureConsoleIntegration } from '@sentry/core'
import type { App as Vue } from 'vue'
import type { Plugin } from 'vue'
import { version } from '~~/package.json'
import { isExpectedApiNetworkControlError } from '~/utils/apiError'

const INFO_SAMPLE_RATE_APP = 0.0 // Send 0% of 'info' events
const DEBUG_SAMPLE_RATE_APP = 0.0 // Send 0% of 'debug' events
const WARNING_SAMPLE_RATE_APP = 0.0 // Send 0% of 'warning' events

const INFO_SAMPLE_RATE_WEB = 0.0 // Send 0% of 'info' events
const DEBUG_SAMPLE_RATE_WEB = 0.0 // Send 0% of 'debug' events
const WARNING_SAMPLE_RATE_WEB = 0.0 // Send 0% of 'warning' events

export const sentryPlugin: Plugin = {
  install(app, _options) {
    const config = useRuntimeConfig()
    const isProduction = config.public.deployEnv === 'production'
    const isLocalDev = config.public.deployEnv === 'local'
    const sentryDsn = config.public.sentryDsn
    const environment = config.public.deployEnv
    if (!isLocalDev) {
      initSentry(app, isProduction, sentryDsn, environment)
      console.info('Sentry Initialized')
    } else {
      // For local development, we do not initialize Sentry
      console.warn('Sentry is not initialized in local development mode')
    }
  }
}

function initSentry(vueApp: Vue, isProduction: boolean, dsn: string, environment: string) {
  // Configure sentry for App
  if (isPlatform('capacitor')) {
    Sentry.init(
      {
        app: vueApp,
        dsn,
        environment,
        release: 'grilld@' + version,
        dist: '1',
        tracesSampleRate: 0,
        debug: false,
        beforeSend(event, _hint) {
          if (isExpectedApiNetworkControlError(getEventExceptionError(event))) {
            return null
          }
          // Always send error-level events or events in non-production environments
          if (event.environment !== 'production' || event.level === 'error' || event.level === 'fatal') {
            return event // Send the event
          }

          // Apply sampling for other log levels
          if (event.level === 'info' || event.level === 'log') {
            if (Math.random() < INFO_SAMPLE_RATE_APP) {
              return event
            }
            return null // Drop the event
          }

          if (event.level === 'debug') {
            if (Math.random() < DEBUG_SAMPLE_RATE_APP) {
              return event
            }
            return null
          }

          if (event.level === 'warning') {
            if (Math.random() < WARNING_SAMPLE_RATE_APP) {
              return event
            }
            return null
          }

          return event // Send events of other levels by default
        },
        beforeBreadcrumb: (breadcrumb) => {
          if (breadcrumb.message === '[object Object]') {
            return null
          }
          if (breadcrumb.category === 'console') {
            switch (breadcrumb.level) {
              case undefined:
                return null
              case 'debug':
              case 'info':
              case 'warning':
              case 'error':
              default:
                break
            }
          }

          return breadcrumb
        },
        integrations: [
          captureConsoleIntegration({
            levels: ['error']
          }),
          SentrySibling.breadcrumbsIntegration({
            console: true
          })
        ]
      },
      SentrySibling.init
    )
  } else {
    // Configure sentry for web
    SentrySibling.init({
      app: vueApp,
      dsn,
      environment,
      release: 'grilld@' + version,
      dist: '1',
      tracesSampleRate: 0,
      beforeSend(event, _hint) {
        if (isExpectedApiNetworkControlError(getEventExceptionError(event))) {
          return null
        }
        // Always send error-level events or events in non-production environments
        if (event.environment !== 'production' || event.level === 'error' || event.level === 'fatal') {
          return event // Send the event
        }

        // Apply sampling for other log levels
        if (event.level === 'info' || event.level === 'log') {
          if (Math.random() < INFO_SAMPLE_RATE_WEB) {
            return event
          }
          return null // Drop the event
        }

        if (event.level === 'debug') {
          if (Math.random() < DEBUG_SAMPLE_RATE_WEB) {
            return event
          }
          return null
        }

        if (event.level === 'warning') {
          if (Math.random() < WARNING_SAMPLE_RATE_WEB) {
            return event
          }
          return null
        }

        return event // Send events of other levels by default
      },
      beforeBreadcrumb: (breadcrumb) => {
        if (breadcrumb.category === 'console') {
          switch (breadcrumb.level) {
            case undefined:
            case 'debug':
              return null
            case 'info':
            case 'warning':
            case 'error':
            default:
              break
          }
        }

        return breadcrumb
      },
      integrations: [
        captureConsoleIntegration({
          levels: ['error']
        }),
        SentrySibling.breadcrumbsIntegration({
          console: true
        })
      ]
    })
  }
}

function getEventExceptionError(event: {
  exception?: { values?: Array<{ type?: string; value?: string }> }
}): Error | undefined {
  const exception = event.exception?.values?.[0]
  if (!exception?.value) return undefined

  const error = new Error(exception.value)
  error.name = exception.type ?? 'Error'
  return error
}
